import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, FileText, Table } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';

interface ExportOptions {
  format: 'csv' | 'json';
  dateRange: '1m' | '3m' | '6m' | '1y' | 'all';
  includeBodyComp: boolean;
  includeRunning: boolean;
  includeGoals: boolean;
  includePhotos: boolean;
}

export const DataExportDialog: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'csv',
    dateRange: '3m',
    includeBodyComp: true,
    includeRunning: true,
    includeGoals: true,
    includePhotos: false,
  });

  const getDateLimit = (range: string) => {
    const now = new Date();
    switch (range) {
      case '1m': return new Date(now.setMonth(now.getMonth() - 1)).toISOString().split('T')[0];
      case '3m': return new Date(now.setMonth(now.getMonth() - 3)).toISOString().split('T')[0];
      case '6m': return new Date(now.setMonth(now.getMonth() - 6)).toISOString().split('T')[0];
      case '1y': return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString().split('T')[0];
      default: return null;
    }
  };

  const fetchExportData = async () => {
    if (!user) return {};
    
    const dateLimit = getDateLimit(exportOptions.dateRange);
    const data: any = {};

    // Body composition data
    if (exportOptions.includeBodyComp) {
      let bodyQuery = supabase
        .from('body_composition')
        .select('*')
        .order('recorded_date', { ascending: true });
      
      if (dateLimit) {
        bodyQuery = bodyQuery.gte('recorded_date', dateLimit);
      }
      
      const { data: bodyData } = await bodyQuery;
      data.bodyComposition = bodyData || [];
    }

    // Running data
    if (exportOptions.includeRunning) {
      let runQuery = supabase
        .from('running_performance')
        .select('*')
        .order('recorded_date', { ascending: true });
      
      if (dateLimit) {
        runQuery = runQuery.gte('recorded_date', dateLimit);
      }
      
      const { data: runData } = await runQuery;
      data.runningPerformance = runData || [];
    }

    // Goals data
    if (exportOptions.includeGoals) {
      const { data: goalsData } = await supabase
        .from('fitness_goals')
        .select('*')
        .order('created_at', { ascending: true });
      
      data.fitnessGoals = goalsData || [];
    }

    // Photos metadata (not actual files)
    if (exportOptions.includePhotos) {
      let photoQuery = supabase
        .from('progress_photos')
        .select('id, upload_date, photo_type, photo_position, tags, notes, is_favorite')
        .order('upload_date', { ascending: true });
      
      if (dateLimit) {
        photoQuery = photoQuery.gte('upload_date', dateLimit);
      }
      
      const { data: photoData } = await photoQuery;
      data.progressPhotos = photoData || [];
    }

    return data;
  };

  const convertToCSV = (data: any) => {
    const csvFiles: Record<string, string> = {};
    
    Object.entries(data).forEach(([key, values]: [string, any]) => {
      if (!Array.isArray(values) || values.length === 0) return;
      
      const headers = Object.keys(values[0]);
      const csvContent = [
        headers.join(','),
        ...values.map((row: any) => 
          headers.map(header => {
            const value = row[header];
            if (value === null || value === undefined) return '';
            if (typeof value === 'string' && value.includes(',')) return `"${value}"`;
            return String(value);
          }).join(',')
        )
      ].join('\n');
      
      csvFiles[key] = csvContent;
    });
    
    return csvFiles;
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    setIsExporting(true);
    
    try {
      const data = await fetchExportData();
      const timestamp = new Date().toISOString().split('T')[0];
      
      if (exportOptions.format === 'csv') {
        const csvFiles = convertToCSV(data);
        
        if (Object.keys(csvFiles).length === 0) {
          toast({
            title: "No Data",
            description: "No data available for the selected options.",
            variant: "destructive",
          });
          return;
        }
        
        // Download individual CSV files
        Object.entries(csvFiles).forEach(([key, content]) => {
          downloadFile(content, `fitness-${key}-${timestamp}.csv`, 'text/csv');
        });
        
      } else {
        // JSON export
        const jsonContent = JSON.stringify(data, null, 2);
        downloadFile(jsonContent, `fitness-data-${timestamp}.json`, 'application/json');
      }
      
      toast({
        title: "Export Complete",
        description: `Your fitness data has been exported successfully.`,
      });
      
      setIsOpen(false);
      
    } catch (error) {
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Export Data
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export Fitness Data</DialogTitle>
          <DialogDescription>Choose what data to export and in which format</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Format */}
          <div>
            <Label htmlFor="format">Export Format</Label>
            <Select 
              value={exportOptions.format} 
              onValueChange={(value: 'csv' | 'json') => 
                setExportOptions(prev => ({ ...prev, format: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">
                  <div className="flex items-center gap-2">
                    <Table className="h-4 w-4" />
                    CSV (Spreadsheet)
                  </div>
                </SelectItem>
                <SelectItem value="json">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    JSON (Raw Data)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div>
            <Label htmlFor="date-range">Date Range</Label>
            <Select 
              value={exportOptions.dateRange} 
              onValueChange={(value: any) => 
                setExportOptions(prev => ({ ...prev, dateRange: value }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1m">Last Month</SelectItem>
                <SelectItem value="3m">Last 3 Months</SelectItem>
                <SelectItem value="6m">Last 6 Months</SelectItem>
                <SelectItem value="1y">Last Year</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Data Types */}
          <div className="space-y-3">
            <Label>Data to Include</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="body-comp"
                  checked={exportOptions.includeBodyComp}
                  onCheckedChange={(checked) => 
                    setExportOptions(prev => ({ ...prev, includeBodyComp: !!checked }))
                  }
                />
                <Label htmlFor="body-comp">Body Composition</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="running"
                  checked={exportOptions.includeRunning}
                  onCheckedChange={(checked) => 
                    setExportOptions(prev => ({ ...prev, includeRunning: !!checked }))
                  }
                />
                <Label htmlFor="running">Running Performance</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="goals"
                  checked={exportOptions.includeGoals}
                  onCheckedChange={(checked) => 
                    setExportOptions(prev => ({ ...prev, includeGoals: !!checked }))
                  }
                />
                <Label htmlFor="goals">Fitness Goals</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="photos"
                  checked={exportOptions.includePhotos}
                  onCheckedChange={(checked) => 
                    setExportOptions(prev => ({ ...prev, includePhotos: !!checked }))
                  }
                />
                <Label htmlFor="photos">Photo Metadata</Label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};